/**
 * Top Customers Table
 *
 * Displays top customers by spend with visual bars
 */

import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { LockedChartCard } from "./locked-chart-card";
import { MetricTooltip } from "./metric-tooltip";

interface TopCustomersTableProps {
  data: Array<{
    customerId: string;
    customerName: string;
    totalSpent: number;
    visitCount: number;
    averageSpent: number;
  }> | null;
}

export function TopCustomersTable({ data }: TopCustomersTableProps) {
  const { t } = useTranslation("reports");

  // If data is null (basic tier), show locked version
  if (data === null) {
    const mockContent = (
      <div className="space-y-2">
        {[
          { name: "Customer A", visits: 12, barWidth: 100 },
          { name: "Customer B", visits: 10, barWidth: 85 },
          { name: "Customer C", visits: 8, barWidth: 70 },
          { name: "Customer D", visits: 7, barWidth: 55 },
          { name: "Customer E", visits: 6, barWidth: 40 },
        ].map((customer, index) => (
          <div key={index} className="flex items-center gap-3">
            {/* Customer info */}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-xs truncate">{customer.name}</div>
              <div className="text-[10px] text-muted-foreground">
                {customer.visits} {t("charts.topCustomers.visits").toLowerCase()}
              </div>
            </div>

            {/* Spend bar */}
            <div className="flex-1 relative h-5 bg-muted rounded overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-green-400"
                style={{ width: `${customer.barWidth}%` }}
              />
            </div>

            {/* Total spent */}
            <div className="text-right">
              <div className="font-semibold text-xs whitespace-nowrap">€0,000</div>
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <LockedChartCard
        title={t("charts.topCustomers.title")}
        tooltip={t("charts.topCustomers.tooltip")}
        mockContent={mockContent}
        height="auto"
      />
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("charts.topCustomers.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t("common.noCustomerData")}</p>
        </CardContent>
      </Card>
    );
  }

  const maxSpent = Math.max(...data.map((c) => c.totalSpent));

  return (
    <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center">
          {t("charts.topCustomers.title")}
          <MetricTooltip content={t("charts.topCustomers.tooltip")} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {data.map((customer, index) => {
            const barWidth = (customer.totalSpent / maxSpent) * 100;
            return (
              <div
                key={customer.customerId}
                className="flex items-center gap-3 animate-in fade-in slide-in-from-left-4 duration-500"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Customer info */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-xs truncate">{customer.customerName}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {customer.visitCount} {t("charts.topCustomers.visits").toLowerCase()}
                  </div>
                </div>

                {/* Spend bar */}
                <div className="flex-1 relative h-5 bg-muted rounded overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-green-400 transition-all duration-1000 ease-out"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>

                {/* Total spent */}
                <div className="text-right">
                  <div className="font-semibold text-xs whitespace-nowrap">
                    <PriceDisplay amount={customer.totalSpent} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
